import {
	Component,
	HostBinding,
	inject,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DocumentDialogComponent } from '@components/document-dialog';
import {
	Authors,
	DOCUMENT_TREE_MODE,
	DocumentTreeComponent,
	EditDocumentMeta,
	MoveDocumentEvent,
	MoveDocumentsToFolderEvent,
	SortChange,
} from '@components/document-tree/document-tree.component';
import { Logger } from '@core/logger';
import {
	getDocumentHtmlId,
	transformBusinessDocumentToDocument,
} from '@core/util/documents';
import { createId } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { getFullName } from '@core/util/string';
import { BusinessUser, DismissedItems } from '@models/businesses';
import {
	BusinessDocumentV2,
	DocumentV2Html,
	DocumentLocation,
	MatterDocumentFlows,
} from '@models/documents';
import { Department, Jurisdiction } from '@models/jurisdictions';
import { DefaultDocument, Document, Folder } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { BusinessDocumentHtmlRepoService } from '@repos/business-document-html/business-document-html.service';
import { JurisdictionDepartmentRepo } from '@repos/jurisdiction-departments/jurisdiction-departments-repo.service';
import { MatterDocumentsRepoService } from '@repos/matter-documents/matter-documents-repo.service';
import { DocumentService } from '@services/document/document.service';
import { NavigationService } from '@services/nav/nav.service';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { BillingBusinessTypesState } from '@state/billing-business';
import { SetDismissedItems } from '@state/business-users/business-users.actions';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { DocumentAndFoldersLoaded } from '@state/global/documents/documents-folders-loaded';
import { MergedMatterDocumentSelectors } from '@state/global/documents/merged-documents';
import { MergedMatterFolderSelectors } from '@state/global/documents/merged-folders';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import {
	SelectedDocuments,
	SelectedMatterDocumentsState,
} from '@state/global/documents/selected-matter-documents.state';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { DocumentsDispatch } from '@state/matter/documents';
import { MatterFoldersDispatch } from '@state/matter/folders/folders.dispatch';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserProfileState } from '@state/user-profile';
import {
	SetDocumentsSort,
	SetExpandAllDocuments,
} from '@state/user-profile/actions';
import { format } from 'date-fns';
import { isEqual } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	take,
	withLatestFrom,
} from 'rxjs/operators';
import { FilesAndFoldersDropdownActions } from 'src/app/private/manage/documents/templates/templates.component';
import { LeapSdkService } from 'src/app/public/leap/leap-sdk.service';
import { LeapApiService } from 'src/app/shared/api-repos/leap/leap-api.service';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DocumentCreationService } from 'src/app/shared/services/document-creation/document-creation.service';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { UrlService } from '../../../../shared/services/url/url.service';
import {
	ContextDialogComponent,
	ContextDialogData,
	ContextDialogResult,
} from './context-dialog/context-dialog.component';
import {
	DocumentDeleteDialogComponent,
	DocumentDialogClose,
	DocumentDialogData,
} from './document-delete-dialog/document-delete-dialog.component';
import {
	FolderDeleteDialogComponent,
	FolderDialogClose,
	FolderDialogData,
} from './folder-delete-dialog/folder-delete-dialog.component';
import {
	FolderEditDialogComponent,
	FolderEditDialogData,
} from './folder-edit-dialog/folder-edit-dialog.component';
import {
	FolderMoveDialogClose,
	FolderMoveDialogComponent,
	FolderMoveDialogData,
} from './folder-move-dialog/folder-move-dialog.component';
import {
	MergeDocumentDialogClose,
	MergeDocumentDialogComponent,
	MergeDocumentDialogData,
} from './merge-document-dialog';
import {
	NewDocumentDialogComponent,
	NewDocumentDialogData,
} from './new-document-dialog';

export interface ContextType {
	clients?: { referenceId?: string }[];
	debts?: { referenceId?: string }[];
	encumbrances?: { referenceId?: string }[];
	mortgages?: { referenceId?: string }[];
	otherParty?: { referenceId?: string }[];
	requisitions?: { referenceId?: string }[];
	undertakings?: { referenceId?: string }[];
}

enum DownloadOptions {
	combined = 'combined',
	individual = 'individual',
}

const DownloadDropdownItems: DropdownOption<DownloadOptions>[] = [
	{ name: 'Combined', value: DownloadOptions.combined },
	{ name: 'Individual', value: DownloadOptions.individual },
];

const addDocumentDropdownItems: DropdownOption<FilesAndFoldersDropdownActions>[] =
	[
		{
			name: 'New folder',
			value: FilesAndFoldersDropdownActions.ADD_FOLDER,
			icon: ICON.FOLDER_PLUS,
		},
		{
			name: 'New blank document',
			value: FilesAndFoldersDropdownActions.ADD_BLANK_DOCUMENT,
			icon: ICON.UPLOAD_FILE,
		},
	];

const leapAddDocumentDropdownItems: DropdownOption<FilesAndFoldersDropdownActions>[] =
	[
		{
			name: 'New blank document',
			value: FilesAndFoldersDropdownActions.ADD_BLANK_DOCUMENT,
			icon: ICON.UPLOAD_FILE,
		},
	];

@UntilDestroy()
@Component({
	selector: 'app-sale-docs',
	templateUrl: './sale-docs.component.html',
	standalone: false,
})
export class SaleDocsComponent implements OnInit {
	noLastEditedBy = '';
	AuthorIds$: Observable<Authors>;
	drawerService = inject(DrawerService);

	@Select(BusinessUsersState.getUsers) businessUsers$!: Observable<
		BusinessUser[]
	>;
	@SelectSnapshot(BusinessBaseDocxsState.getBaseDocxPathForOffice)
	getBaseDocxPathForOfficeFn: (officeId: string, referenceId: string) => string;
	@SelectSnapshot(SelectedMatter.officeId) officeId: string;
	@ViewChild('documentTree') documentTree!: DocumentTreeComponent;
	@Select(MergedMatterDocumentSelectors.getAllForMatters)
	documents$: Observable<Document[]>;
	documentTreeMode = DOCUMENT_TREE_MODE.MATTER;
	@Select(MergedMatterFolderSelectors.getAll) folders$: Observable<Folder[]>;
	@SelectSnapshot(MergedMatterFolderSelectors.getFolderByIdFn)
	getFolderByIdFn: (folderId: string) => Folder;
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@Select(SelectedMatterDocumentsState.highlightedDocumentIds)
	highlightedDocumentIds$!: Observable<string[]>;

	@Select(SelectedMatter.getEnabledDocumentFlow)
	enabledDocumentFlow$!: Observable<MatterDocumentFlows>;
	@Select(UserProfileState.documentsExpandAll)
	documentsExpandAll$!: Observable<boolean>;
	@SelectSnapshot(UserProfileState.documentsSortProperty)
	documentsSortProperty!: string;
	@SelectSnapshot(UserProfileState.documentsSortDirection)
	documentsSortDirection!: string;
	@Select(BusinessUsersState.dismissedItems)
	dismissedItems$: Observable<DismissedItems>;

	@Select(SelectedMatterDocumentsState.getSelectedDocuments)
	selectedDocuments$!: Observable<SelectedDocuments>;

	@SelectSnapshot(SelectedMatter.matterFileTypeLabel)
	matterFileTypeLabel: string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.getLeapMatterId) leapMatterId: string;
	@Select(DocumentAndFoldersLoaded.areDocumentsAndFoldersLoading)
	areDocumentsAndFoldersLoading$: Observable<boolean>;

	@SelectSnapshot(SelectedMatter.matterSource) matterSource: string;

	@Select(SelectedMatterDocumentsState.getViewingDocumentPDF)
	mergedPdf$: Observable<Blob>;
	@ViewSelectSnapshot(SelectedMatterDocumentsState.getLoading)
	selectedDocumentStateLoading: boolean;
	@Select(BillingBusinessTypesState.getIsFreeTrial)
	isFreeTrial$: Observable<boolean>;
	@Select(MatterJurisdictionSelectors.getAll) jurisdictions$: Observable<
		Jurisdiction[]
	>;

	private enabledDocumentFlow: MatterDocumentFlows = MatterDocumentFlows.HTML;
	@SelectSnapshot(MatterClients.get) clients: Clients[];

	public preventLeapDocumentClick = false;

	@HostBinding('class.overflow-y-hidden')
	@HostBinding('class.h-auto')
	@HostBinding('class.max-h-full')
	stretchVertically = true;

	searchText: string;
	showDropdownOptions = false;
	documentDropdownOptions = addDocumentDropdownItems;
	leapDocumentDropdownOptions = leapAddDocumentDropdownItems;
	ICON = ICON;
	downloadDropdownItems = DownloadDropdownItems;
	sortBy!: string;
	sortOrder!: string;
	error: boolean;
	creating: boolean;
	jurisdictionDepartments: { [id: string]: Department[] } = {};
	folders: Folder[];
	// wait 20 seconds before opening the document
	private openingDelay = 20000;

	public MatterDocumentFlows = MatterDocumentFlows;

	constructor(
		private documentRepo: MatterDocumentsRepoService,
		private navigationService: NavigationService,
		private documentService: DocumentService,
		private documentCreationService: DocumentCreationService,
		public jurisdictionDepartmentRepo: JurisdictionDepartmentRepo,
		private dialog: MatDialog,
		private documentsDispatch: DocumentsDispatch,
		private matterFoldersDispatch: MatterFoldersDispatch,
		private router: Router,
		private selectedMatterDocumentsDispatch: SelectedMatterDocumentsDispatch,
		private snackBarService: SnackbarService,
		private store: Store,
		private businessTemplateHtmlRepoService: BusinessDocumentHtmlRepoService,
		private leapSDKService: LeapSdkService,
		private leapApiService: LeapApiService,
	) {
		this.searchText = '';

		this.AuthorIds$ = combineLatest([this.documents$, this.folders$]).pipe(
			untilDestroyed(this),
			map(([documents, folders]) =>
				[
					...new Set([
						...documents.map((document) => document.author),
						...documents.map((document) => document.lastEditedBy),
						...folders.map((folder) => folder.author),
					]),
				]
					.filter((authorId) => !!authorId)
					.sort(),
			),
			distinctUntilChanged(
				(prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
			),
			map((authorIds) => new Set(authorIds)),
			switchMap((authorIds) =>
				this.businessUsers$.pipe(
					map((users) => users.filter((user) => authorIds.has(user.id))),
					map((users) => {
						const authors: Authors = {
							'': this.noLastEditedBy,
						};
						users.forEach(({ id, firstName, lastName }) => {
							authors[id] = getFullName([firstName, lastName]);
						});

						return authors;
					}),
				),
			),
		);

		this.folders$.pipe(untilDestroyed(this)).subscribe((folders) => {
			this.folders = folders;
		});

		this.jurisdictions$
			.pipe(untilDestroyed(this))
			.subscribe((jurisdictions) => {
				jurisdictions.forEach((jurisdiction) => {
					this.jurisdictionDepartmentRepo
						.All({ jurisdictionId: jurisdiction.id })
						.pipe(take(1))
						.subscribe((departments: Department[]) => {
							this.jurisdictionDepartments[jurisdiction.id] = departments;
						});
				});
			});
	}

	toggleExpandAll(expandAll: boolean): void {
		this.store.dispatch(new SetExpandAllDocuments(expandAll));
	}

	onSortChange({ column, order }: SortChange) {
		this.store.dispatch(new SetDocumentsSort(column, order));
	}

	onDismissAddDocumentBanner() {
		this.store.dispatch(new SetDismissedItems({ addDocument: true }));
	}

	ngOnInit(): void {
		this.sortBy = this.documentsSortProperty;
		this.sortOrder = this.documentsSortDirection;

		this.enabledDocumentFlow$!.pipe(untilDestroyed(this)).subscribe(
			(loaded) => {
				this.enabledDocumentFlow = loaded;
			},
		);
	}

	private getInitials(name: string): string {
		if (!name) return '';

		const words = name.split(' ');

		return words.length > 1
			? words
					.map((word) => word.charAt(0))
					.join('')
					?.toUpperCase()
			: name.charAt(0)?.toUpperCase();
	}

	onDropdownAction(
		event: DropdownResult<DropdownOption<FilesAndFoldersDropdownActions>>,
	): void {
		switch (event.result.value) {
			case FilesAndFoldersDropdownActions.ADD_DOCUMENT:
				this.addDocument(false);
				break;
			case FilesAndFoldersDropdownActions.ADD_BLANK_DOCUMENT:
				this.addDocument(true);
				break;
			case FilesAndFoldersDropdownActions.ADD_FOLDER:
				this.addFolder();
				break;
		}
	}

	download() {
		this.selectedMatterDocumentsDispatch.DownloadSelectedDocumentsIndividual();
	}

	toggleDropdown() {
		this.showDropdownOptions = !this.showDropdownOptions;
	}

	hideDropdown() {
		this.showDropdownOptions = false;
	}

	addDocument(createNewSelected: boolean, folderId = ''): void {
		const data: NewDocumentDialogData = {
			folderId,
			preFiterFn: (document) => !document?.disableManualAddToMatter,
			createNewSelected,
		};

		this.dialog
			.open(NewDocumentDialogComponent, {
				data,
				maxWidth: '600px',
				minWidth: '300px',
				maxHeight: '530px',
				width: '50%',
				panelClass: 'no-padding',
			})
			.afterClosed()
			.pipe(take(1));
	}

	addFolder(parentId = ''): void {
		const FolderData: FolderEditDialogData = {
			author: this.loggedInUser.uid,
			folder: null,
			parentId,
		};

		this.dialog
			.open(FolderEditDialogComponent, {
				data: FolderData,
				maxWidth: '350px',
				width: '95vw',
			})
			.afterClosed()
			.pipe(
				take(1),
				filter((newFolder) => !!newFolder),
			)
			.subscribe((newFolder) => {
				this.matterFoldersDispatch.Create(newFolder);
				this.documentTree.expandFolder(parentId);
			});
	}

	async checkForContext(doc: BusinessDocumentV2) {
		const html: DocumentV2Html = (
			await this.businessTemplateHtmlRepoService
				.Get(getDocumentHtmlId(doc))
				.pipe(take(1))
				.toPromise()
		).data() as DocumentV2Html;
		const newDocument = {
			...transformBusinessDocumentToDocument(doc),
			html: html.html,
		};
		if (doc.groupType) {
			this.dialog
				.open<ContextDialogComponent, ContextDialogData>(
					ContextDialogComponent,
					{
						data: {
							document: doc,
							jurisdictionDepartments: this.jurisdictionDepartments,
						},
						panelClass: 'no-padding',
						maxWidth: '500px',
					},
				)
				.afterClosed()
				.pipe(take(1))
				.subscribe(async (result: ContextDialogResult) => {
					if (result) {
						this.createDocument(
							{ ...newDocument, context: result?.context },
							result?.item?.name,
						);
					}
				});
		} else {
			this.createDocument(newDocument);
		}
	}

	// TODO: Move to document-creation.service.ts
	async createDocument(doc: Document, folderName?: string) {
		this.error = false;
		this.creating = true;
		const id = createId();

		const newDocument: Document = {
			...this.documentService.getDefaultDocument(),
			...doc,
			author: this.loggedInUser.uid,
			date: format(Date.now(), 'yyyy-MM-dd'),
			source: doc.source,
			createdAt: new Date().getTime(),
			context: doc.context || {},
			id,
			alwaysExists: false,
			disableManualAddToMatter: false,
			updatedAt: undefined,
		};

		if (doc.context && Object.keys(doc.context)?.length) {
			const folderId = this.folders.find((folder) =>
				isEqual(folder.context, doc.context),
			)?.id;
			if (folderId) {
				newDocument.folderId = folderId;
			}
		}

		try {
			await this.documentRepo.createDocuments(
				{ [id]: newDocument },
				this.matterId,
			);
			this.navigationService.goToMatter_Documents_Doc(this.matterId, id);
			const successText = folderName
				? `This document was created and added to ${folderName}`
				: 'This document was created';
			this.snackBarService.openSnackBar({ text: successText, type: 'success' });
		} catch (err) {
			Logger.error(`Unable to create documents: ${err}`);
		}
	}

	onEndItemClick(): void {
		this.searchText = '';
	}

	copyDocument(documentId: string): void {
		this.getDocumentFromId$(documentId)
			.pipe(
				take(1),
				filter((doc) => !!doc),
				map(
					(selectedDocument) =>
						({
							...DefaultDocument,
							...selectedDocument,
							alwaysExists: false,
							author: this.loggedInUser.uid,
							createdAt: new Date().getTime(),
							id: createId(),
							protected: false,
							source: '',
							wasVirtualized: false,
							virtual: false,
							title: `${selectedDocument.title} - COPY`,
						}) as Document,
				),
			)
			.subscribe(async (newDocument) => {
				// Only do it for documents created with the new structure that have a htmlId in them. Old docs won't have htmlId.
				if (newDocument.htmlId && !newDocument.html) {
					const html = (
						await this.businessTemplateHtmlRepoService
							.Get(newDocument.htmlId)
							.pipe(take(1))
							.toPromise()
					).data() as DocumentV2Html;
					newDocument.html = html.html;
					newDocument.originalHtml = html.html;
				}

				this.documentsDispatch.Create(newDocument);
			});
	}

	deleteDocument(documentId: string): void {
		this.documents$
			.pipe(
				take(1),
				map((documents) =>
					documents.find((document) => document.id === documentId),
				),
				filter((document) => !!document),
				switchMap((document) =>
					this.dialog
						.open<
							DocumentDeleteDialogComponent,
							DocumentDialogData,
							DocumentDialogClose
						>(DocumentDeleteDialogComponent, {
							data: {
								document,
							},
							maxWidth: '550px',
							width: '95vw',
						})
						.afterClosed(),
				),
				take(1),
				filter((deletedDocument) => !!deletedDocument),
			)
			.subscribe(({ id, title }) => {
				this.documentsDispatch.Remove(id);
				this.snackBarService.openSnackBar({
					text: `<span class='font-bold'>${title}</span> has been deleted.`,
					type: 'success',
				});
				this.selectedMatterDocumentsDispatch.UnhighlightDocumentIds([id]);
			});
	}

	deleteFolder(folderId: string): void {
		this.getFolderFromId$(folderId)
			.pipe(
				take(1),
				filter((folder) => !!folder),
				withLatestFrom(this.folders$, this.documents$),
				switchMap(([matchedFolder, folders, documents]) => {
					if (
						folders.some((folder) => folder.parentId === folderId) ||
						documents.some((document) => document.folderId === folderId)
					) {
						return this.dialog
							.open<
								FolderDeleteDialogComponent,
								FolderDialogData,
								FolderDialogClose
							>(FolderDeleteDialogComponent, {
								data: {
									folder: matchedFolder,
								},
								maxWidth: '350px',
								width: '95vw',
							})
							.afterClosed();
					}

					return of(matchedFolder);
				}),
				filter((folder) => !!folder),
			)
			.subscribe(({ id, title }) => {
				this.matterFoldersDispatch.Remove(id);
				this.snackBarService.openSnackBar({
					text: `<span class='font-bold'>${title}</span> has been deleted.`,
					type: 'success',
				});
			});
	}

	async editDocument({ documentId }: EditDocumentMeta): Promise<void> {
		try {
			document?.getSelection()?.removeAllRanges();

			const doc = await this.getDocumentFromId$(documentId)
				.pipe(take(1))
				.toPromise();

			if (!doc) {
				throw new Error('Document not found');
			}

			this.handleHtmlDocumentClick(doc);
		} catch (e) {
			Logger.error(e);
		}
	}

	getCustomDocumentName(document: Document): string {
		//LEAP PENDING REFACTOR
		// Gets the groupType name used in the folder to use it as part of the document name in LEAP, since they don't exist inside folders
		if (document?.folderId) {
			const folder = this.getFolderByIdFn(document.folderId);

			if (!folder || !folder.title) return document.title;

			const parser = new DOMParser()
				?.parseFromString(folder?.title, 'text/html')
				?.querySelector('body');
			const groupTypeName = parser?.childNodes?.[1]?.textContent;

			if (groupTypeName) {
				return `${document.title} - ${groupTypeName}`;
			}
		}

		return document.title;
	}

	handleHtmlDocumentClick(document: Document): void {
		if (document?.collection?.length) {
			this.drawerService.openDrawer(DocumentDialogComponent, {
				title: document.title,
				data: {
					documentId: document.id,
					showStateSelector: false,
					title: document.title,
				},
				noStyle: true,
			});
		} else {
			void this.router.navigateByUrl(
				UrlService.Matter_Documents_Document(this.matterId, document.id),
			);
		}
	}

	editFolder(folderId: string): void {
		this.getFolderFromId$(folderId)
			.pipe(
				take(1),
				filter((folder) => !!folder),
				switchMap((folder) => {
					const data: FolderEditDialogData = {
						author: folder.author,
						folder,
						parentId: folder.parentId,
					};

					return this.dialog
						.open(FolderEditDialogComponent, {
							data,
							maxWidth: '350px',
							width: '95vw',
						})
						.afterClosed();
				}),
				filter((folder) => !!folder), // ensure its been removed
			)
			.subscribe((updatedFolder) => {
				this.matterFoldersDispatch.Edit(updatedFolder);
			});
	}

	get rootLabel(): string {
		return `${this.matterFileTypeLabel}: Home`;
	}

	moveDocument(documentId: MoveDocumentEvent): void {
		const { rootLabel } = this;
		this.getDocumentFromId$(documentId)
			.pipe(
				take(1),
				switchMap((document) =>
					this.dialog
						.open<
							FolderMoveDialogComponent,
							FolderMoveDialogData,
							FolderMoveDialogClose
						>(FolderMoveDialogComponent, {
							data: { document, rootLabel },
							maxWidth: '600px',
							minWidth: '300px',
							width: '50%',
						})
						.afterClosed()
						.pipe(
							filter((folderId) => !!folderId || folderId === ''),
							switchMap((folderId) => {
								this.documentCreationService.moveDocumentToFolder(
									document,
									folderId,
								);
								return of(folderId);
							}),
							switchMap((folderId) => {
								return this.getFolderFromId$(folderId);
							}),
							map((folder) => ({ folder, document })),
						),
				),
			)
			.subscribe(({ folder, document }) => {
				this.clearSelected();
				this.snackBarService.openSnackBar({
					text: `<span class='font-bold'>${document.title}</span> has been moved to <span class='font-bold'>${
						folder?.title || this.rootLabel
					}</span>`,
					type: 'info',
				});
			});
	}

	moveDocumentsToFolder({
		documents,
		folderId,
	}: MoveDocumentsToFolderEvent): void {
		this.documentCreationService.moveDocumentsToFolder(
			documents as Document[],
			folderId,
		);
		this.getFolderFromId$(folderId)
			.pipe(
				take(1),
				filter((folder) => !!folder || folderId === ''),
			)
			.subscribe((folder) => {
				const folderTitle = folder?.title || this.rootLabel;
				let text = `<span class='font-bold'>${documents[0]?.title}</span> has been moved to <span class='font-bold'>${folderTitle}</span>`;
				if (documents.length > 1) {
					text = `<span class='font-bold'>${documents.length} documents</span> moved to <span class='font-bold'>${folderTitle}</span>`;
				}
				this.clearSelected();
				this.snackBarService.openSnackBar({
					text,
					type: 'info',
				});
			});
	}

	onDeselectAllClick(): void {
		this.clearSelected();
	}

	openMergedDocumentDialog(): void {
		combineLatest([this.highlightedDocumentIds$, this.documents$])
			.pipe(take(1))
			.subscribe(([selectedDocumentIds, documents]) => {
				const numberSelected = selectedDocumentIds.length;
				const data: MergeDocumentDialogData = {
					documents: selectedDocumentIds.map((documentId) =>
						documents.find((document) => document.id === documentId),
					),
					location: DocumentLocation.MATTER,
				};

				if (numberSelected >= 2) {
					this.dialog
						.open<
							MergeDocumentDialogComponent,
							MergeDocumentDialogData,
							MergeDocumentDialogClose
						>(MergeDocumentDialogComponent, {
							data,
							width: '650px',
						})
						.afterClosed()
						.pipe(
							take(1),
							filter((closeData) => !!closeData),
						)
						.subscribe(({ documentId, title }) => {
							if (title) {
								this.snackBarService.openSnackBar({
									text: `${title} created from ${numberSelected} merged files.`,
									type: 'success',
								});
							}
							if (documentId) {
								this.documentTree.selectedDocumentIds = new Set([documentId]);
							}
						});
				}
			});
	}

	print() {
		this.mergedPdf$.pipe(take(1)).subscribe((pdf) => {
			const blob = new Blob([pdf], { type: 'application/pdf' });
			const blobURL = URL.createObjectURL(blob);

			const iframe = document.createElement('iframe'); //load content in an iframe to print later
			document.body.appendChild(iframe);

			iframe.style.display = 'none';
			iframe.src = blobURL;
			iframe.onload = () => {
				setTimeout(() => {
					iframe.focus();
					iframe.contentWindow.print();
				}, 1);
			};
		});
	}

	resetDocument(documentId: string): void {
		this.documentCreationService.resetDocument(documentId);
	}

	updateSelectedDocuments(documentIds: string[]): void {
		this.selectedMatterDocumentsDispatch.HighlightDocumentIds(documentIds);
	}

	private getDocumentFromId$(documentId: string): Observable<Document> {
		return this.documents$.pipe(
			map((documents) =>
				documents.find((document) => document.id === documentId),
			),
		);
	}

	private getFolderFromId$(folderId: string): Observable<Folder> {
		return this.folders$.pipe(
			take(1),
			map((folders) => folders.find((folder) => folder.id === folderId)),
		);
	}

	private clearSelected(): void {
		this.documentTree.clearSelected();
	}
}
